import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
);

const Subsection = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    {children}
  </div>
);

const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-3">{children}</p>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-6 text-gray-700 space-y-1.5 mb-3">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">FieldHouse Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-10">Effective Date: July 3, 2026</p>

          <Section title="1. Introduction">
            <P>
              FieldHouse Solutions, LLC (&quot;FieldHouse,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates the FieldHouse App, a mobile and web-based platform designed to help youth sports leagues, teams, coaches, players, and families organize their sporting lives and pursuits (the &quot;App&quot; or &quot;Services&quot;).
            </P>
            <P>
              We are deeply committed to protecting the privacy of all our users, especially minors. This Privacy Policy explains what information we collect, how we use and protect it, with whom we share it, and the rights you have regarding your information.
            </P>
            <P>
              By using the App, you agree to the practices described in this Privacy Policy. If you do not agree, please discontinue use of the App.
            </P>
          </Section>

          <Section title="2. Key Definitions">
            <P>For purposes of this Privacy Policy:</P>
            <Ul items={[
              '"User" means any person who accesses or uses the App, including League Admins, Team Admins, Coaches, Players, and Parent/Guardian accounts.',
              '"Minor" means any individual under the age of 18.',
              '"Parent/Guardian" means the parent or legal guardian of a Minor who has created or authorized a Player account.',
              '"League Admin" or "Team Admin" means a User with administrative privileges to manage a league, team, or organization within the App.',
              '"Player" means an athlete registered in the App, which may include Minors.',
              '"Player Data" means information submitted about a Player, including performance data, evaluations, and biomechanical analysis.',
              '"Personal Information" means information that identifies or could reasonably be used to identify an individual.',
            ]} />
          </Section>

          <Section title="3. Who This Policy Covers">
            <P>This Privacy Policy applies to all Users of the App, including:</P>
            <Ul items={[
              'League and Team Administrators',
              'Coaches and evaluators',
              'Players, including Minors',
              'Parents and Guardians',
              'Spectators or fans who access shared content',
            ]} />
            <P>
              Because our App is intended for use by youth sports organizations and will regularly involve Minors, we apply heightened privacy protections as described in Section 7 below.
            </P>
          </Section>

          <Section title="4. Information We Collect">
            <Subsection title="4.1 Information You Provide Directly">
              <P>
                We collect information you provide when you create an account, set up a team, or use features of the App, such as:
              </P>
              <Ul items={[
                'Account registration details: name, email address, username, password, and phone number',
                'Profile information: role (coach, player, parent, admin), sport(s), team affiliation, jersey number, position',
                'For Minors: date of birth, grade level, and parental/guardian contact information',
                'League and tournament details: schedules, rosters, game results, standings',
                'Communications sent through in-app messaging and notification features',
                'Payment information for premium features, processed by a third-party payment processor; we do not store full card details',
                'Survey responses, feedback, or support requests you submit to us',
                'Pictures of minors used solely for tryout evaluation purposes; pictures disappear in 30 days and are not stored after that period by FieldHouse Solutions, LLC',
              ]} />
            </Subsection>

            <Subsection title="4.2 Player Performance and Evaluation Data">
              <P>
                A core feature of the App is player evaluation and AI-powered analysis. Among other things, we collect and process subjective evaluations and ratings entered by coaches or evaluators, video or image submissions uploaded for analysis, AI-generated feedback, scores, and technique assessments derived from video analysis, statistical performance data (for example, pitch velocity, batting stats, game scores), and/or drill results and practice activity logs.
              </P>
              <P>
                Video and images submitted for analysis may capture the likeness of Minors. We use this data solely to provide technique feedback and improvement recommendations. We do not use this data for advertising, profiling unrelated to athletic performance, or sale to third parties.
              </P>
            </Subsection>

            <Subsection title="4.3 Information Collected Automatically">
              <P>When you use the App, we automatically collect certain technical information including, but not limited to:</P>
              <Ul items={[
                'Device information: device type, operating system, unique device identifiers',
                'Usage data: pages or features accessed, time spent, search queries, feature interactions',
                'IP address and approximate location derived from IP',
                'Log data: crash reports, error logs, and diagnostic information',
                'Cookies and similar tracking technologies (see Section 9)',
              ]} />
            </Subsection>

            <Subsection title="4.4 Information from Third Parties">
              <P>
                We may receive information about you from League or Team Admins who register players and add roster data on their behalf, social login providers (for example, Google or Apple) if you choose to authenticate via those services, and/or third-party analytics tools that help us understand App usage patterns.
              </P>
            </Subsection>
          </Section>

          <Section title="5. How We Use Your Information">
            <P>We use the information we collect to:</P>
            <Ul items={[
              'Create and manage user accounts and profiles',
              'Operate league and tournament management features, including scheduling, rosters, standings, communications, budget, and cash flow',
              'Provide AI-powered analysis and personalized athletic feedback',
              'Enable coach evaluations and player development tracking',
              'Facilitate in-app messaging and notifications between coaches, players, and parents',
              'Process payments for premium features or subscriptions',
              'Send transactional communications, including account confirmations, schedule updates, and evaluation results',
              'Improve and develop our App, algorithms, and AI models',
              'Detect fraud, prevent abuse, and maintain the security of the App',
              'Comply with legal obligations',
              'With your consent, send marketing or promotional communications, with opt-out available at any time',
            ]} />
            <P>
              We may use de-identified or aggregated biomechanical data to improve our AI models and technique analysis systems. We will not use personally identifiable video of Minors to train AI models without explicit, informed parental consent.
            </P>
          </Section>

          <Section title="6. How We Share Your Information">
            <P>We do not sell Personal Information. We may share information in the following limited circumstances:</P>

            <Subsection title="6.1 Within the App Platform">
              <P>
                Player Data and evaluations may be visible to the League Admin, Team Admin, and Coach(es) associated with the player&apos;s team, as configured by the admin. Schedules, rosters, and results may also be shared with other members of the same team or league as directed by the Admin. Further, Parents/Guardians linked to a Player account can view that player&apos;s evaluations, feedback, and communications.
              </P>
            </Subsection>

            <Subsection title="6.2 Service Providers">
              <P>We share data with trusted vendors who help us operate the App, including:</P>
              <Ul items={[
                'Cloud hosting and data storage providers',
                'Payment processors for subscription billing',
                'Video analysis and AI infrastructure providers',
                'Customer support platforms',
                'Analytics and crash-reporting services',
                'Our SMS/text messaging provider (Twilio), solely to deliver the messages you have opted in to receive',
              ]} />
              <P>
                All service providers are required to protect your data, use it only for the services they provide to us, and comply with applicable privacy laws.
              </P>
            </Subsection>

            <Subsection title="6.3 Legal and Safety Disclosures">
              <P>We may disclose information when we believe in good faith that disclosure is necessary to:</P>
              <Ul items={[
                'Comply with applicable laws, regulations, or legal process',
                'Protect the rights, property, or safety of our users, the public, or our company',
                'Investigate or prevent fraud, abuse, or violations of our Terms of Service',
              ]} />
            </Subsection>

            <Subsection title="6.4 Business Transfers">
              <P>
                In the event of a merger, acquisition, or sale of all or part of our business, Personal Information may be transferred to the acquiring entity. We will notify users via the App or email prior to any such transfer and before data becomes subject to a different privacy policy.
              </P>
            </Subsection>

            <Subsection title="6.5 With Your Consent">
              <P>
                We may share information in other ways with your explicit consent, including consent obtained from a Parent/Guardian for a Minor.
              </P>
            </Subsection>

            <Subsection title="6.6 Mobile Numbers and SMS Consent">
              <P>
                <strong>We do not sell or share mobile phone numbers or SMS opt-in consent with third parties for marketing or promotional purposes, under any circumstances.</strong>{' '}
                Mobile numbers collected for text messaging are used solely to deliver the FieldHouse account, team, and organization updates you have opted in to receive, and are shared only with our SMS delivery provider (Twilio) as necessary to send those messages. See our{' '}
                <a href="/sms-compliance" className="text-blue-600 underline">SMS Compliance</a> page for how SMS consent works.
              </P>
            </Subsection>
          </Section>

          <Section title="7. Children's Privacy and COPPA Compliance">
            <P>
              Protecting the privacy of children is especially important to us. Because our App is designed for youth sports, it will regularly involve users under the age of 13. We comply with the Children&apos;s Online Privacy Protection Act (COPPA) and other applicable laws protecting minors.
            </P>

            <Subsection title="7.1 Users Under 13">
              <P>
                Players under the age of 13 may not create independent accounts. Their accounts must be created and managed by a verified Parent or Guardian, or by a Team/League Admin with documented parental consent.
              </P>
              <P>We will not collect Personal Information from children under 13 without verifiable parental consent.</P>
              <P>
                Parents and Guardians may review, request correction of, or request deletion of their child&apos;s Personal Information at any time by contacting us at{' '}
                <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">support@fieldhouse.club</a>.
              </P>
              <P>We do not serve behavioral advertising to users known to be under 13.</P>
              <P>
                Video and images of children under 13 submitted for biomechanical analysis will be used solely to generate athletic feedback and will not be shared, sold, or used to train AI models without explicit parental consent.
              </P>
            </Subsection>

            <Subsection title="7.2 Users Ages 13-17">
              <P>
                Teen users (ages 13-17) may register with parental or guardian authorization, as required by the League or Team Admin during registration.
              </P>
              <P>We recommend that Parents/Guardians review this Privacy Policy with their teens and monitor their use of the App.</P>
              <P>We do not engage in targeted advertising directed at users known to be under 18.</P>
              <P>
                California residents under 18 may request removal of content they have publicly posted in the App by contacting us as described below.
              </P>
            </Subsection>

            <Subsection title="7.3 Parental Rights">
              <P>Parents or Guardians of Minor players have the right to:</P>
              <Ul items={[
                "Review the Personal Information we have collected about their child",
                "Request correction or deletion of their child's Personal Information",
                "Revoke consent for continued collection or use of their child's data",
                'Opt out of any non-essential data uses, including AI training',
              ]} />
              <P>
                To exercise these rights, contact us at{' '}
                <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">support@fieldhouse.club</a>.
              </P>
            </Subsection>
          </Section>

          <Section title="8. AI-Powered Analysis">
            <P>
              Our App offers a distinctive feature: AI-driven analysis of athletic technique using video or image submissions. This section specifically addresses how we handle data in connection with this feature.
            </P>
            <P>
              Videos and images are submitted voluntarily by the user, coach, or authorized administrator. Analysis is performed to generate feedback on technique and is delivered to the submitting user, coach, and/or linked Parent/Guardian.
            </P>
            <P>
              Video data is stored securely and retained only as long as necessary to deliver the analysis and maintain the player&apos;s development history, or until deletion is requested.
            </P>
            <P>
              We do not share identifiable video or images with third parties except as needed to operate the AI analysis feature, including with our AI infrastructure providers under strict data processing agreements. De-identified, aggregated biomechanical data with no personally identifiable attributes may be used to improve our AI models and analysis accuracy.
            </P>
            <P>
              For Minor players, identifiable video will not be used for AI training purposes without explicit, documented parental consent.
            </P>
          </Section>

          <Section title="9. Cookies and Tracking Technologies">
            <P>We use cookies and similar technologies, such as pixels and SDKs, to:</P>
            <Ul items={[
              'Keep you logged in and remember your preferences',
              'Understand how the App is used and improve its performance',
              'Detect and prevent fraud or abuse',
              'Measure the effectiveness of features and communications',
            ]} />
            <P>
              We do not use third-party advertising cookies or behavioral tracking technologies for interest-based advertising. You may adjust cookie settings in your browser or device, though some features may not function correctly if cookies are disabled.
            </P>
          </Section>

          <Section title="10. Data Security">
            <P>
              We take security seriously and implement administrative, technical, and physical safeguards designed to protect your information, including:
            </P>
            <Ul items={[
              'Encryption of data in transit (TLS/SSL) and at rest',
              'Password hashing and secure credential storage',
              'Role-based access controls limiting who can view sensitive data',
              'Regular security assessments and vulnerability monitoring',
              'Data minimization practices - we collect only what is necessary',
            ]} />
            <P>
              No system is perfectly secure. In the event of a data breach affecting your Personal Information, we will notify affected users and applicable regulators as required by law.
            </P>
          </Section>

          <Section title="11. Data Retention">
            <P>We retain your Personal Information for as long as:</P>
            <Ul items={[
              'Your account is active or as needed to provide you the Services',
              'Required by law or for legitimate business purposes, such as tax records and dispute resolution',
              'Necessary to maintain the integrity of league records, such as historical results, unless deletion is requested',
            ]} />
            <P>
              Upon account deletion or a valid deletion request, we will delete or de-identify your Personal Information within a reasonable time, except where retention is required by law.
            </P>
            <P>
              Video data submitted for biomechanical analysis will be deleted upon request or after 30 days of inactivity, whichever is earlier.
            </P>
          </Section>

          <Section title="12. Your Privacy Rights">
            <P>Depending on where you live, you may have the following rights regarding your Personal Information:</P>
            <Ul items={[
              'Right to Access: Request a copy of the Personal Information we hold about you',
              'Right to Correct: Request correction of inaccurate information',
              'Right to Delete: Request deletion of your Personal Information',
              'Right to Restrict or Object: Limit how we use your data in certain circumstances',
              'Right to Data Portability: Receive your data in a portable format',
              'Right to Opt Out: Opt out of any applicable sale or sharing of data for targeted advertising; note that we do not sell data',
              'Right to Non-Discrimination: We will not discriminate against you for exercising your privacy rights',
            ]} />
            <P>
              Parents and Guardians may exercise these rights on behalf of their Minor children. To submit a request, contact us at{' '}
              <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">support@fieldhouse.club</a>. We will endeavor to respond within the timeframe required by applicable law, generally 30-45 days.
            </P>

            <Subsection title="12.1 State-Specific Rights">
              <P>
                Residents of states with comprehensive privacy laws have additional rights as described under applicable law, including the right to appeal denied requests. To submit an appeal, contact us at{' '}
                <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">support@fieldhouse.club</a> with the subject line &quot;Privacy Request Appeal.&quot;
              </P>
            </Subsection>
          </Section>

          <Section title="13. Communications and Notifications">
            <P>
              We may send you transactional messages: account confirmations, schedule updates, evaluation results, and payment receipts. These cannot be opted out of while your account is active. We may also send in-app notifications of messages from coaches, admins, or team communications, configurable in app settings.
            </P>
            <P>
              You may also receive marketing emails, including information about product updates, feature announcements, or promotions, and SMS messages.
            </P>
          </Section>

          <Section title="14. Third-Party Links and Services">
            <P>
              The App may contain links to third-party websites, platforms, or services. We are not responsible for the privacy practices of those third parties. We encourage you to review the privacy policies of any external services you access through the App.
            </P>
          </Section>

          <Section title="15. Changes to This Privacy Policy">
            <P>We may update this Privacy Policy from time to time. When we make material changes, we will:</P>
            <Ul items={[
              'Post the updated policy in the App with a new Effective Date',
              'Notify registered users',
              "For changes affecting Minors' data, obtain renewed parental consent where required by law",
            ]} />
            <P>
              Your continued use of the App after the effective date of a revised policy constitutes your acceptance of the changes.
            </P>
          </Section>

          <Section title="16. Contact Us">
            <P>If you have questions, concerns, or requests related to this Privacy Policy, please contact us:</P>
            <div className="bg-gray-50 rounded-lg p-5 text-gray-700 text-sm space-y-1">
              <p><strong>FieldHouse Solutions, LLC</strong></p>
              <p>
                Email:{' '}
                <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">
                  support@fieldhouse.club
                </a>
              </p>
              <p>
                Website:{' '}
                <a href="https://www.fieldhouse.club" className="text-blue-600 underline">
                  www.fieldhouse.club
                </a>
              </p>
            </div>
            <P>
              For requests regarding a Minor&apos;s data, please include the child&apos;s name, your relationship to the child, and a description of your request.
            </P>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
